import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { useAgentStore } from "@/stores/agent-store";
import { fetchTourHint, postTourHintEvent } from "@/lib/agent-sessions";
import { availableEntries, pendingVersions, latestVersion, runWhatsNew, type WhatsNewEntry } from "@/lib/whats-new";

// Topbar help button, two jobs: guided tours and versioned "what's new" presentations.
// - Main page click: a choice menu "Start the tour | Show what's new" with a countdown that
//   auto-picks from the stored state (never toured → tour; toured → what's new). No pending
//   news → straight to /tour-all.
// - Other pages: the page/app tour menu.
// - Nag popover: never-toured users get "start the tour" (server-counted timeouts); toured
//   users with unseen news get "See what's new" (dismissing marks the news seen; a timeout
//   just hides it until the next app load).

const HINT_TIMEOUT_MS = 20_000;
const TOUR_CHOICE_TIMEOUT_MS = 10_000;
const MENU_ITEM = "block w-full rounded-md px-3 py-2 text-left transition hover:bg-ink/[0.06]";

export function TourHintLauncher() {
  const intl = useIntl();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const ask = useAgentStore((s) => s.ask);
  const [hint, setHint] = useState<"tour" | "news" | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [choiceOpen, setChoiceOpen] = useState(false);
  const [started, setStarted] = useState(false);
  const [pending, setPending] = useState<readonly WhatsNewEntry[]>([]);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  // Distinguishes "user acted" (posts its own event) from "still open at teardown" (no post).
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchTourHint(ctrl.signal)
      .then((state) => {
        if (ctrl.signal.aborted) return;
        const news = pendingVersions(state.newsVersion ?? null);
        setStarted(Boolean(state.started));
        setPending(news);
        if (!state.started && state.show) {
          setHint("tour");
          timerRef.current = setTimeout(() => {
            timerRef.current = null;
            setHint(null);
            // Ignored for the full 20s — count it (server stops nagging after enough of these).
            void postTourHintEvent("timeout").catch(() => undefined);
          }, HINT_TIMEOUT_MS);
        } else if (state.started && news.length) {
          setHint("news");
          // A news timeout is NOT counted: the popover simply returns on the next app load
          // until the user sees or dismisses the presentation.
          timerRef.current = setTimeout(() => {
            timerRef.current = null;
            setHint(null);
          }, HINT_TIMEOUT_MS);
        }
      })
      .catch(() => undefined); // cosmetic feature; a failed check shows nothing
    return () => {
      ctrl.abort();
      if (timerRef.current) clearTimeout(timerRef.current); // unmount ≠ timeout, don't count it
    };
  }, []);

  // Click/tap outside closes the menus (ServiceMenu pattern). Closing the choice menu cancels
  // its auto-pick.
  useEffect(() => {
    if (!menuOpen && !choiceOpen) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) {
        setMenuOpen(false);
        setChoiceOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen, choiceOpen]);

  const closeHint = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setHint(null);
  };

  const startTour = (command: "/tour" | "/tour-all") => {
    closeHint();
    setMenuOpen(false);
    setChoiceOpen(false);
    void postTourHintEvent("started").catch(() => undefined);
    ask(command); // opens the drawer and queues the command (handleSend runs the tour)
  };

  const showWhatsNew = () => {
    closeHint();
    setMenuOpen(false);
    setChoiceOpen(false);
    // Nothing pending (all seen) → replay the newest available presentation instead.
    const entries = pending.length ? pending : availableEntries().slice(-1);
    void runWhatsNew(entries, { navigate, intl }); // marks the latest version seen afterwards
    setPending([]);
  };

  // The choice menu's countdown: expiry auto-picks by stored state (toured → what's new).
  useEffect(() => {
    if (!choiceOpen) return;
    const timer = setTimeout(() => {
      setChoiceOpen(false);
      if (started && pending.length) showWhatsNew();
      else startTour("/tour-all");
    }, TOUR_CHOICE_TIMEOUT_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers are stable per open
  }, [choiceOpen]);

  // The news option exists whenever the catalog has an available entry (replay included).
  const newsAvailable = availableEntries().length > 0;
  // What the choice-menu timer picks when the user does nothing (toured + unseen news → news).
  const autoPick: "tour" | "news" = started && pending.length ? "news" : "tour";

  const onHelpClick = () => {
    closeHint();
    // Main page: ALWAYS the tour/news selection (with auto-pick countdown); other pages get
    // the page/app/news menu.
    if (pathname === "/") setChoiceOpen((v) => !v);
    else setMenuOpen((v) => !v);
  };

  const label = intl.formatMessage({ id: "topbar.help.label", defaultMessage: "Start the guided tour" });

  return (
    // While the hint is open, the button itself pulses with the tour's highlight outline —
    // otherwise it is not clear WHICH of the topbar icons the popover means.
    <span ref={wrapperRef} className={`relative inline-flex ${hint ? "assistant-highlight" : ""}`}>
      <button
        type="button"
        onClick={onHelpClick}
        aria-label={label}
        title={label}
        className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ink transition hover:bg-ink/[0.06] hover:text-ink"
      >
        {/* Same stroke weight as the sibling launcher icons (bell / chat: 1.5). */}
        <svg viewBox="0 0 24 24" aria-hidden="true" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <circle cx="12" cy="12" r="9" />
          <path d="M9.6 9.3a2.4 2.4 0 1 1 3.3 2.9c-.7.4-.9.8-.9 1.7" strokeLinecap="round" />
          <circle cx="12" cy="16.6" r="0.4" fill="currentColor" stroke="none" />
        </svg>
      </button>
      {choiceOpen && (
        <div
          role="menu"
          className="box absolute right-0 top-11 z-40 w-56 rounded-lg bg-surface p-1.5 text-smycles-meta leading-snug text-ink shadow-lg"
        >
          <span className="absolute -top-1 right-3 h-2 w-2 rotate-45 bg-surface" aria-hidden />
          {/* The countdown bar sits INSIDE the option the timer will pick — the indicator of
              what happens when the user does nothing. */}
          <button
            type="button"
            role="menuitem"
            onClick={() => startTour("/tour-all")}
            className={`${MENU_ITEM} ${autoPick === "tour" ? "bg-ink/[0.04] font-medium" : ""}`}
          >
            <FormattedMessage id="topbar.help.choiceTour" defaultMessage="Start the tour" />
            {autoPick === "tour" && <ChoiceCountdown />}
          </button>
          {newsAvailable && (
            <button
              type="button"
              role="menuitem"
              onClick={showWhatsNew}
              className={`${MENU_ITEM} ${autoPick === "news" ? "bg-ink/[0.04] font-medium" : ""}`}
            >
              <FormattedMessage id="topbar.help.choiceNews" defaultMessage="Show what's new" />
              {autoPick === "news" && <ChoiceCountdown />}
            </button>
          )}
        </div>
      )}
      {menuOpen && (
        <div
          role="menu"
          className="box absolute right-0 top-11 z-40 w-56 rounded-lg bg-surface p-1.5 text-smycles-meta leading-snug text-ink shadow-lg"
        >
          <span className="absolute -top-1 right-3 h-2 w-2 rotate-45 bg-surface" aria-hidden />
          <button
            type="button"
            role="menuitem"
            onClick={() => startTour("/tour")}
            className={MENU_ITEM}
          >
            <FormattedMessage id="agent.tour.starterChip" defaultMessage="Show me around this page" />
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => startTour("/tour-all")}
            className={MENU_ITEM}
          >
            <FormattedMessage id="agent.tour.starterChipAll" defaultMessage="Show me around the app" />
          </button>
          {newsAvailable && (
            <button
              type="button"
              role="menuitem"
              onClick={showWhatsNew}
              className={MENU_ITEM}
            >
              <FormattedMessage id="topbar.help.choiceNews" defaultMessage="Show what's new" />
            </button>
          )}
        </div>
      )}
      {hint && (
        <div
          role="status"
          className="box absolute right-0 top-11 z-40 w-64 rounded-lg bg-surface p-3 text-smycles-meta leading-snug text-ink shadow-lg"
        >
          {/* Arrow up, pointing at the button. */}
          <span className="absolute -top-1 right-3 h-2 w-2 rotate-45 bg-surface" aria-hidden />
          <div className="flex items-start gap-2">
            <p className="min-w-0 flex-1">
              {hint === "news" ? (
                <FormattedMessage
                  id="topbar.help.hintNews"
                  defaultMessage="Something has changed since your tour. This button shows you what's new."
                />
              ) : (
                <FormattedMessage
                  id="topbar.help.hint"
                  defaultMessage="New here? Start the guided tour of the app with this button."
                />
              )}
            </p>
            <button
              type="button"
              onClick={() => {
                const wasNews = hint === "news";
                closeHint();
                if (wasNews) {
                  // Discarding counts as seen — the news never nags again.
                  const latest = latestVersion();
                  if (latest) void postTourHintEvent("news-seen", latest).catch(() => undefined);
                  setPending([]);
                } else {
                  void postTourHintEvent("dismissed").catch(() => undefined);
                }
              }}
              aria-label={intl.formatMessage({ id: "topbar.help.hintDismiss", defaultMessage: "Dismiss" })}
              className="grid h-5 w-5 shrink-0 place-items-center rounded text-ink-muted transition hover:bg-ink/[0.08] hover:text-ink"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3 w-3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </span>
  );
}

// The auto-pick indicator inside a choice-menu option: fills over the decision window.
function ChoiceCountdown() {
  return (
    <span className="mt-1.5 block h-1 overflow-hidden rounded-full bg-ink/10" role="presentation">
      <span
        className="block h-full origin-left rounded-full bg-accent"
        style={{ animation: `assistant-tour-countdown ${TOUR_CHOICE_TIMEOUT_MS}ms linear forwards` }}
      />
    </span>
  );
}
